/**
 * Polskie teksty dla czytnika ekranu przy przeciąganiu kolumn (dnd-kit) — domyślne są po
 * angielsku i czytają techniczne identyfikatory zamiast nazw kolumn. Wzorzec jak
 * w `RailSettingsTab` (ustawienia paska bocznego).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Announcements, DndContextProps, UniqueIdentifier } from '@dnd-kit/core';
import type { TFunction } from 'i18next';

/** Kolumna widoczna na przeciąganej liście: id sortowania + nazwa do odczytu. */
export interface DndAnnouncedItem {
  id: string;
  name: string;
}

type DndAccessibility = NonNullable<DndContextProps['accessibility']>;

function buildAccessibility(items: readonly DndAnnouncedItem[], t: TFunction): DndAccessibility {
  const nameOf = (id: UniqueIdentifier): string =>
    items.find((item) => item.id === id)?.name ?? String(id);
  const positionOf = (id: UniqueIdentifier): number =>
    items.findIndex((item) => item.id === id) + 1;
  const total = items.length;

  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      t('tenant.embeddedTable.dnd.start', {
        name: nameOf(active.id),
        position: positionOf(active.id),
        total,
      }),
    onDragOver: ({ active, over }) =>
      over
        ? t('tenant.embeddedTable.dnd.over', {
            name: nameOf(active.id),
            position: positionOf(over.id),
            total,
          })
        : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? t('tenant.embeddedTable.dnd.end', {
            name: nameOf(active.id),
            position: positionOf(over.id),
            total,
          })
        : t('tenant.embeddedTable.dnd.cancel', { name: nameOf(active.id) }),
    onDragCancel: ({ active }) => t('tenant.embeddedTable.dnd.cancel', { name: nameOf(active.id) }),
  };

  return {
    announcements,
    screenReaderInstructions: { draggable: t('tenant.embeddedTable.dnd.instructions') },
  };
}

/**
 * Wartość dla propsa `accessibility` komponentu `DndContext`. Zwraca TĘ SAMĄ referencję, dopóki
 * nie zmieni się treść pozycji (id + nazwa) ani funkcja tłumaczeń — wołający przekazuje świeżą
 * tablicę co render, a `DndContext` nie powinien przez to dostawać nowych obiektów.
 */
export function useDndAccessibility(items: readonly DndAnnouncedItem[]): DndAccessibility {
  const { t } = useTranslation();
  // Klucz z TREŚCI pozycji: długość nazwy przed nazwą rozdziela pola jednoznacznie.
  const key = items
    .map((item) => `${item.id}\u0000${item.name.length}:${item.name}`)
    .join('\u0001');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- liczy się treść pozycji (`key`), nie tożsamość tablicy
  const stableItems = useMemo(() => items, [key]);
  return useMemo(() => buildAccessibility(stableItems, t), [stableItems, t]);
}
